import React from 'react';
import { motion } from 'framer-motion';

const cubeColors = [
  'bg-cube-1', // Blue
  'bg-cube-2', // Pink
  'bg-cube-3', // Yellow
  'bg-cube-4', // Green
  'bg-cube-5', // Purple
];

// Get a deterministic color for a cube based on its index
const getCubeColor = (index) => cubeColors[index % cubeColors.length];

// Cube component with 3D-like appearance
export const Cube = ({ index = 0, size = 'md', animate = true, delay = 0 }) => {
  const sizeClasses = {
    sm: 'w-10 h-10 rounded-md',
    md: 'w-12 h-12 rounded-lg',
    lg: 'w-16 h-16 rounded-xl',
  };

  const colorClass = getCubeColor(index);

  return (
    <motion.div
      initial={animate ? { scale: 0, opacity: 0 } : false}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 20,
        delay: delay * 0.1
      }}
      className={`
        ${sizeClasses[size]}
        ${colorClass}
        relative
        shadow-lg
      `}
      style={{
        boxShadow: `
          inset 0 -4px 0 rgba(0,0,0,0.15),
          inset 0 4px 0 rgba(255,255,255,0.2),
          0 4px 8px rgba(0,0,0,0.15)
        `
      }}
    >
      {/* Shine effect */}
      <div 
        className="absolute inset-0 rounded-inherit"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)',
          borderRadius: 'inherit'
        }}
      />
    </motion.div>
  );
};

// Grid arrangement for displaying cubes
export const CubeGrid = ({ count, size = 'md', animate = true }) => {
  const getLayout = (n) => {
    switch(n) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-2';
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-2';
      case 5: return 'grid-cols-3'; // 3 top, 2 bottom
      default: return 'grid-cols-3';
    }
  };

  const gapClass = size === 'lg' ? 'gap-3' : 'gap-2';

  return (
    <div className={`grid ${getLayout(count)} ${gapClass} place-items-center w-fit mx-auto`}>
      {Array.from({ length: count }, (_, i) => (
        <Cube 
          key={i} 
          index={i} 
          size={size} 
          animate={animate}
          delay={i}
        />
      ))}
    </div>
  );
};

// Box container for cubes (mimics physical cube box)
export const CubeBox = ({ count, showBox = true, covered = false, size = 'md' }) => {
  const boxPadding = size === 'lg' ? 'p-4' : 'p-3';

  return (
    <div className="relative">
      {showBox && (
        <div 
          className={`
            ${boxPadding}
            bg-card
            border-2 border-border
            rounded-2xl
            shadow-card
            relative
            overflow-hidden
          `}
        >
          <CubeGrid count={count} size={size} animate={!covered} />
          
          {/* Cover overlay */}
          {covered && (
            <motion.div
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -100, opacity: 0 }}
              className="absolute inset-0 bg-muted rounded-2xl flex items-center justify-center"
            >
              <span className="text-4xl">🎁</span>
            </motion.div>
          )}
        </div>
      )}
      {!showBox && (
        <CubeGrid count={count} size={size} />
      )}
    </div>
  );
};

// Animated cube that enters or leaves
export const AnimatedCubeEnter = ({ count, onComplete }) => {
  return (
    <motion.div
      initial={{ x: 100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      onAnimationComplete={onComplete}
    >
      <Cube index={count} size="md" animate={false} />
    </motion.div>
  );
};

export const AnimatedCubeLeave = ({ index, onComplete }) => {
  return (
    <motion.div
      initial={{ x: 0, opacity: 1 }}
      animate={{ x: -100, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      onAnimationComplete={onComplete}
    >
      <Cube index={index} size="md" animate={false} />
    </motion.div>
  );
};

export default CubeGrid;
